import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Landing.css";

/* ── CountUp ─────────────────────────────────────────────────── */
function CountUp({ end, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60;
        const step = end / steps;
        let current = 0;
        const timer = setInterval(() => {
          current += step;
          if (current >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(current));
        }, 1800 / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);
  return <span ref={ref} className="stat-num">{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ── Data ────────────────────────────────────────────────────── */
const TRACK_CATEGORIES = [
  {
    id: "hardware", label: "Hardware", icon: "⚡",
    color: "#f59e0b", bg: "rgba(245,158,11,0.10)",
    tracks: [
      { title: "VLSI Design Fundamentals", tags: ["Verilog", "CMOS", "Logic Design"], phases: 4, students: 147, badge: "Top Rated" },
      { title: "PCB Design & Prototyping", tags: ["KiCad", "Schematic", "Layout"], phases: 5, students: 93, badge: "New" },
      { title: "Embedded Systems Mastery", tags: ["ARM", "RTOS", "C", "Peripherals"], phases: 6, students: 218, badge: "Hot" },
    ],
  },
  {
    id: "software", label: "Software", icon: "💻",
    color: "#3b82f6", bg: "rgba(59,130,246,0.10)",
    tracks: [
      { title: "Frontend Internship Track", tags: ["HTML", "CSS", "React"], phases: 5, students: 284, badge: "Most Enrolled" },
      { title: "Python for Data Science", tags: ["Python", "NumPy", "ML Intro"], phases: 6, students: 391, badge: "Popular" },
      { title: "System Design Mastery", tags: ["Architecture", "Databases", "APIs"], phases: 5, students: 176, badge: "Pro" },
    ],
  },
  {
    id: "research", label: "Research", icon: "🔬",
    color: "#8b5cf6", bg: "rgba(139,92,246,0.10)",
    tracks: [
      { title: "ML Research Foundations", tags: ["Linear Algebra", "Backprop", "PyTorch"], phases: 7, students: 203, badge: "Rigorous" },
      { title: "Signal Processing Track", tags: ["DSP", "FFT", "Filters"], phases: 5, students: 88, badge: "Niche" },
      { title: "Computer Vision Basics", tags: ["OpenCV", "CNN", "Datasets"], phases: 5, students: 112, badge: "New" },
    ],
  },
];

const HW_TRACKS_ALL = [
  { title: "VLSI Foundations", tags: ["Verilog", "CMOS", "Logic Design"], phases: 5, students: 10, badge: "Top Rated" },
  { title: "RTL Design Career Path", tags: ["IC Projects", "SystemVerilog", "UVM"], phases: 3, students: 15, badge: "New" },
  { title: "Embedded Systems Mastery", tags: ["ARM", "RTOS", "C", "Peripherals"], phases: 5, students: 20, badge: "Hot" },
  { title: "ARM Cortex-M & STM32", tags: ["Projects", "STM32", "FreeRTOS"], phases: 5, students: 18, badge: "New" },
];

const PHASES = [
  { n: 1, title: "CMOS & Gate Fundamentals", resources: 3, weight: 15, done: true },
  { n: 2, title: "Combinational Logic Design", resources: 2, weight: 20, done: true },
  { n: 3, title: "Sequential Circuits & FSMs", resources: 2, weight: 25, active: true },
  { n: 4, title: "Verilog RTL Design", resources: 2, weight: 25 },
  { n: 5, title: "Timing & Synthesis", resources: 3, weight: 15 },
];

const PLATFORM_FEATURES = [
  {
    id: "tracks", icon: "🗺️", label: "Curated Tracks",
    headline: "Not playlists. Complete learning paths.",
    desc: "Every track is structured into phases — each phase has handpicked free resources and ends with an AI quiz that contributes a weighted % to your readiness score. Hardware or software, you always know where you are.",
  },
  {
    id: "playlist", icon: "🎬", label: "Playlist → Course",
    headline: "Turn any YouTube playlist into a structured course.",
    desc: "Paste a playlist URL. Learnlia auto-structures it into phases, generates AI quizzes per phase, and makes it completable with a readiness score — in under 60 seconds.",
  },
  {
    id: "roadmap", icon: "🧭", label: "Custom Roadmap",
    headline: "Build a roadmap for your exact goal.",
    desc: "Tell Learnlia your role, your goal, and your current level. It assembles a personalised track order from existing curated content — so you always know what to learn next.",
  },
  {
    id: "profile", icon: "🏆", label: "Proof Profile",
    headline: "A shareable profile recruiters can verify.",
    desc: "Every quiz you pass, every track you complete — it builds your public readiness profile at learnlia.com/u/yourname. Recruiters see real evidence of completion backed by AI assessment.",
  },
];

/* ── Small components ────────────────────────────────────────── */
function FeaturePill({ icon, text }) {
  return (
    <div className="fpill">
      <span className="fpill-icon">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <button className="faq-header" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <span className={`faq-icon ${open ? "rotate" : ""}`}>+</span>
      </button>
      <div ref={ref} className="faq-content"
        style={{ height: open ? ref.current?.scrollHeight + "px" : "0px" }}>
        <div className="faq-answer">{answer}</div>
      </div>
    </div>
  );
}

/* ── Feature panel mini-visuals ─────────────────────────────── */
function MiniTracks() {
  return (
    <div className="mini-card">
      <div className="mini-label">Browse by category</div>
      {[
        { icon: "⚡", name: "Hardware", count: 4, color: "#f59e0b" },
        { icon: "💻", name: "Software", count: 12, color: "#3b82f6" },
        { icon: "🔬", name: "Research", count: 3, color: "#8b5cf6" },
      ].map(c => (
        <div key={c.name} className="mini-cat-row" style={{ borderLeftColor: c.color }}>
          <span style={{ fontSize: 15 }}>{c.icon}</span>
          <div style={{ flex: 1 }}>
            <div className="mini-cat-name">{c.name}</div>
            <div className="mini-cat-sub">{c.count} tracks live</div>
          </div>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
        </div>
      ))}
      <div className="mini-phase-bar-row">
        {[15, 20, 25, 25, 15].map((w, i) => (
          <div key={i} className={`mini-phase-seg ${i < 2 ? "done" : i === 2 ? "active" : ""}`} style={{ flex: w }} />
        ))}
        <span className="mini-readiness-label">68% readiness</span>
      </div>
    </div>
  );
}

function MiniPlaylist() {
  return (
    <div className="mini-card">
      <div className="mini-label">Paste a playlist URL</div>
      <div className="mini-url-bar">
        <span style={{ color: "#ef4444", fontSize: 13 }}>▶</span>
        <span className="mini-url-text">youtube.com/playlist?list=PLxxxx</span>
        <span className="mini-url-btn">Convert</span>
      </div>
      <div className="mini-arrow">↓ AI structuring...</div>
      {["Introduction & Overview", "Core Concepts", "Hands-on Labs", "Advanced Topics"].map((p, i) => (
        <div key={i} className="mini-phase-row">
          <span className="mini-phase-n">{i + 1}</span>
          <span style={{ flex: 1, fontSize: 12, color: "var(--text)" }}>{p}</span>
          <span className="mini-quiz-chip">AI quiz</span>
        </div>
      ))}
    </div>
  );
}

function MiniRoadmap() {
  return (
    <div className="mini-card">
      <div className="mini-label">Your personalised roadmap</div>
      <div className="mini-goal-chips">
        <span className="mini-chip">Goal: VLSI Engineer</span>
        <span className="mini-chip">Level: Beginner</span>
      </div>
      {[
        { n: 1, title: "Digital Logic Foundations", done: true },
        { n: 2, title: "VLSI Design Fundamentals", active: true },
        { n: 3, title: "FPGA Development Track", locked: true },
        { n: 4, title: "Advanced RTL Design", locked: true },
      ].map((r, i) => (
        <div key={i} className="mini-road-row">
          <div className={`mini-road-dot ${r.done ? "done" : r.active ? "active" : ""}`}>
            {r.done ? "✓" : r.n}
          </div>
          <span className={`mini-road-title ${r.done || r.active ? "solid" : ""}`}>{r.title}</span>
          {r.locked && <span style={{ fontSize: 11, marginLeft: "auto" }}>🔒</span>}
        </div>
      ))}
    </div>
  );
}

function MiniProfile() {
  return (
    <div className="mini-card">
      <div className="mini-label">learnlia.com/u/yourname</div>
      <div className="mini-profile-head">
        <div className="mini-avatar">R</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Rajasekaran M.</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>86 Readiness score</div>
        </div>
      </div>
      {[
        { name: "VLSI Design", pct: 100, color: "#f59e0b" },
        { name: "Frontend Track", pct: 68, color: "#3b82f6" },
        { name: "Embedded Systems", pct: 40, color: "#10b981" },
      ].map(b => (
        <div key={b.name} className="mini-bar-row">
          <span className="mini-bar-name">{b.name}</span>
          <div className="mini-bar-bg">
            <div style={{ height: "100%", width: `${b.pct}%`, background: b.color, borderRadius: 999 }} />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", width: 30, textAlign: "right", flexShrink: 0 }}>{b.pct}%</span>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN LANDING
════════════════════════════════════════════════════════════════ */
export default function Landing() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [heroCategory, setHeroCategory] = useState("hardware");
  const [activeTrack, setActiveTrack] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);

  const activeCat = TRACK_CATEGORIES.find(c => c.id === heroCategory) || TRACK_CATEGORIES[0];

  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: "ease-out-cubic" });
    const t = setInterval(() => {
      setHeroCategory(prev => {
        const ids = TRACK_CATEGORIES.map(c => c.id);
        const next = ids[(ids.indexOf(prev) + 1) % ids.length];
        setActiveTrack(0);
        return next;
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const goStart = () => navigate(user ? "/tracks" : "/tracks/public");

  return (
    <div className="landing">

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg-grid" />
        <div className="hero-orb hero-orb--1" />
        <div className="hero-orb hero-orb--2" />

        <div className="hero-inner">
          <div className="hero-left">
            <div className="hero-eyebrow" data-aos="fade-up">
              <span className="eyebrow-dot" />
              Hardware · Software · Research — all in one place
            </div>

            <h1 className="hero-h1" data-aos="fade-up" data-aos-delay="60">
              The structured path
              <span className="hero-gradient-text">from learner to job-ready.</span>
            </h1>

            <p className="hero-sub" data-aos="fade-up" data-aos-delay="120">
              Whether you're learning VLSI, embedded systems, frontend, or ML —
              Learnlia turns free internet resources into structured tracks you
              actually finish, assessed by AI, proven to recruiters.
            </p>

            <div className="hero-cat-row" data-aos="fade-up" data-aos-delay="145">
              {TRACK_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`hero-cat-btn ${heroCategory === cat.id ? "hero-cat-btn--active" : ""}`}
                  style={{ "--tc": cat.color, "--tc-bg": cat.bg }}
                  onClick={() => { setHeroCategory(cat.id); setActiveTrack(0); }}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>

            <div className="hero-pills" data-aos="fade-up" data-aos-delay="155">
              <FeaturePill icon="⚡" text="Hardware tracks" />
              <FeaturePill icon="💻" text="Software tracks" />
              <FeaturePill icon="🎬" text="Playlist → Course" />
              <FeaturePill icon="🧭" text="Custom roadmaps" />
              <FeaturePill icon="🧠" text="AI quizzes" />
            </div>

            <div className="hero-ctas" data-aos="fade-up" data-aos-delay="190">
              <button className="btn-primary" onClick={goStart}>
                Browse {activeCat.label} Tracks — Free →
              </button>
              <button className="btn-ghost" onClick={() => navigate("/tracks/public")}>
                See all tracks
              </button>
            </div>

            <p className="hero-trust" data-aos="fade-up" data-aos-delay="230">
              <span className="trust-avatars">
                {["R", "U", "N", "D", "A"].map((l, i) => (
                  <span key={i} className="trust-av" style={{ "--i": i }}>{l}</span>
                ))}
              </span>
              <span>Joined by <strong>847+ learners</strong> this month. No credit card.</span>
            </p>
          </div>

          <div className="hero-right" data-aos="fade-left" data-aos-delay="100">
            <div className="hero-track-ui" style={{ "--tc": activeCat.color }}>
              <div className="htu-header">
                <span className="htu-dot htu-dot--red" />
                <span className="htu-dot htu-dot--amber" />
                <span className="htu-dot htu-dot--green" />
                <span className="htu-title">learnlia.com / tracks / {heroCategory}</span>
              </div>

              <div className="htu-tabs">
                {TRACK_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    className={`htu-tab ${heroCategory === cat.id ? "htu-tab--active" : ""}`}
                    style={{ "--tc": cat.color }}
                    onClick={() => { setHeroCategory(cat.id); setActiveTrack(0); }}
                  >
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>

              <div className="htu-cards">
                {activeCat.tracks.map((t, i) => (
                  <button
                    key={i}
                    className={`htc ${activeTrack === i ? "htc--active" : ""}`}
                    style={{ "--tc": activeCat.color }}
                    onClick={() => setActiveTrack(i)}
                  >
                    <div className="htc-top">
                      <span className="htc-cat" style={{ color: activeCat.color, background: activeCat.bg }}>
                        {activeCat.label}
                      </span>
                      <span className="htc-badge">{t.badge}</span>
                    </div>
                    <h3 className="htc-title">{t.title}</h3>
                    <div className="htc-tags">
                      {t.tags.map(tag => <span key={tag} className="htc-tag">{tag}</span>)}
                    </div>
                    <div className="htc-meta">
                      <span>📚 {t.phases} phases</span>
                      <span>👥 {t.students}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="htu-detail" style={{ "--tc": activeCat.color }}>
                <div className="htu-detail-label">Now viewing</div>
                <div className="htu-detail-title">{activeCat.tracks[activeTrack]?.title}</div>
                <div className="htu-phases">
                  {PHASES.slice(0, 4).map(ph => (
                    <div key={ph.n} className={`htu-phase ${ph.done ? "done" : ph.active ? "active" : ""}`}>
                      <span className="htu-phase-dot" />
                      <span className="htu-phase-name">{ph.title}</span>
                      <span className="htu-phase-w">{ph.weight}%</span>
                    </div>
                  ))}
                </div>
                <div className="htu-ai-badge">🧠 AI Assessment ready for Phase 3</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROOF BAR ─────────────────────────────────────────── */}
      <section className="proof-bar" data-aos="fade-up">
        <div className="proof-inner">
          {[
            { end: 847, suffix: "+", label: "Active learners" },
            { end: 7, suffix: "+", label: "Curated tracks" },
            { end: 94, suffix: "%", label: "Resources verified" },
            { end: 3200, suffix: "+", label: "AI quizzes generated" },
            { end: 28, suffix: "+", label: "Tracks completed" },
          ].map(s => (
            <div key={s.label} className="proof-stat">
              <CountUp end={s.end} suffix={s.suffix} />
              <span className="proof-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── HARDWARE SPOTLIGHT ────────────────────────────────── */}
      <section className="hw-spotlight" data-aos="fade-up">
        <div className="section-inner">
          <div className="hw-header">
            <div>
              <div className="section-label hw-section-label">
                ⚡ Hardware Tracks — now on Learnlia
              </div>
              <h2 className="section-h2" style={{ textAlign: "left" }}>
                The internet has great hardware resources.
                <br />
                <span className="hw-gradient-text">Nobody structures them. Until now.</span>
              </h2>
              <p className="section-sub" style={{ textAlign: "left", margin: "0 0 0" }}>
                VLSI, PCB design, embedded systems, FPGA — outstanding free content scattered
                across YouTube, textbooks, and university sites. Learnlia sequences them into
                phases, validates them, and attaches AI quizzes that prove you learned the hard parts.
              </p>
            </div>
            <div className="hw-stat-col">
              <div className="hw-stat-card">
                <span className="hw-stat-num">4</span>
                <span className="hw-stat-label">Hardware tracks live</span>
              </div>
              <div className="hw-stat-card">
                <span className="hw-stat-num">12+</span>
                <span className="hw-stat-label">More tracks Q3</span>
              </div>
            </div>
          </div>

          <div className="hw-track-grid">
            {HW_TRACKS_ALL.map((t, i) => (
              <div key={i} className="hw-card" data-aos="fade-up" data-aos-delay={`${i * 60}`}>
                <div className="hw-card-top">
                  <span className="hw-card-cat">⚡ Hardware</span>
                  <span className="hw-card-badge">{t.badge}</span>
                </div>
                <h3 className="hw-card-title">{t.title}</h3>
                <div className="hw-card-tags">
                  {t.tags.map(tag => <span key={tag} className="hw-tag">{tag}</span>)}
                </div>
                <div className="hw-card-footer">
                  <span>📚 {t.phases} phases</span>
                  <span>👥 {t.students} enrolled</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM FEATURES ─────────────────────────────────── */}
      <section className="features-section" data-aos="fade-up">
        <div className="section-inner">
          <div style={{ textAlign: "center" }}>
            <div className="section-label">Platform features</div>
            <h2 className="section-h2">
              Four ways Learnlia<br />makes you actually finish.
            </h2>
          </div>

          <div className="feat-tabs">
            {PLATFORM_FEATURES.map((f, i) => (
              <button
                key={f.id}
                className={`feat-tab ${activeFeature === i ? "feat-tab--active" : ""}`}
                onClick={() => setActiveFeature(i)}
              >
                <span>{f.icon}</span> {f.label}
              </button>
            ))}
          </div>

          <div className="feat-panel">
            {PLATFORM_FEATURES.map((f, i) => (
              <div key={f.id} className={`feat-pane ${activeFeature === i ? "feat-pane--active" : ""}`}>
                <div className="feat-pane-text">
                  <h3 className="feat-pane-h">{f.headline}</h3>
                  <p className="feat-pane-desc">{f.desc}</p>
                  <button className="btn-primary" onClick={goStart}>Try it free →</button>
                </div>
                <div className="feat-pane-visual">
                  {i === 0 && <MiniTracks />}
                  {i === 1 && <MiniPlaylist />}
                  {i === 2 && <MiniRoadmap />}
                  {i === 3 && <MiniProfile />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────── */}
      <section className="what-section" data-aos="fade-up">
        <div className="section-inner">
          <div style={{ textAlign: "center" }}>
            <div className="section-label">How it works</div>
            <h2 className="section-h2">
              A Track is a complete path —<br />not just a playlist.
            </h2>
            <p className="section-sub">
              Every Learnlia track is structured into phases, each phase has handpicked free resources,
              and each phase ends with an AI quiz that proves you actually learned it.
              Your score builds a public readiness profile.
            </p>
          </div>

          <div className="track-anatomy">
            <div className="ta-phases" data-aos="fade-right" data-aos-delay="100">
              <div className="ta-track-title">
                <span className="ta-cat" style={{ color: "#d97706", background: "rgba(245,158,11,0.08)" }}>
                  ⚡ Hardware
                </span>
                VLSI Design Fundamentals
              </div>
              {PHASES.map(ph => (
                <div key={ph.n} className={`ta-phase ${ph.done ? "ta-phase--done" : ph.active ? "ta-phase--active" : ""}`}>
                  <div className="ta-phase-left">
                    <div className="ta-phase-dot">{ph.done ? "✓" : ph.n}</div>
                    <div className="ta-phase-line" />
                  </div>
                  <div className="ta-phase-body">
                    <div className="ta-phase-name">{ph.title}</div>
                    <div className="ta-phase-meta">
                      <span>🎬 {ph.resources} resources</span>
                      <span>⚖️ {ph.weight}% readiness</span>
                      {ph.done && <span className="ta-done-chip">✓ quiz passed</span>}
                      {ph.active && <span className="ta-active-chip">● in progress</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="ta-values" data-aos="fade-left" data-aos-delay="140">
              {[
                { icon: "🎯", title: "Top 1% resources only", desc: "Every video, doc and article is handpicked — not scraped. We watch it, debate it, then add it if it's genuinely the best way to learn that concept." },
                { icon: "🧠", title: "AI phase assessments", desc: "Finish a phase → take the quiz. Questions are generated from the actual content you just watched. 70% to pass, instant explanations." },
                { icon: "📊", title: "Real readiness score", desc: "Each phase contributes a weighted % to your overall Track Readiness. Pass everything and hit 100 — a recruiter-readable signal of actual skill." },
                { icon: "🔓", title: "Everything free to start", desc: "No paid courses, no resource paywalls. Every YouTube video, MDN article, and doc we curate is 100% free. Pro unlocks unlimited AI quizzes and your full proof profile." },
              ].map(v => (
                <div key={v.title} className="ta-value">
                  <span className="ta-value-icon">{v.icon}</span>
                  <div><h4>{v.title}</h4><p>{v.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── QUALITY ───────────────────────────────────────────── */}
      <section className="quality-section" data-aos="fade-up">
        <div className="qs-bg" />
        <div className="section-inner">
          <div className="section-label">Resource quality</div>
          <h2 className="section-h2">We don't link. We <em>verify.</em></h2>
          <p className="section-sub">
            The internet has millions of tutorials. 90% are mediocre.
            We watch, debate, and stress-test each one — then add only those
            that actually teach the concept clearly.
          </p>
          <div className="quality-grid">
            {[
              { icon: "👁️", title: "Human curation first", desc: "Every resource is reviewed by the Learnlia team before it goes live. No bulk imports, no automated scraping." },
              { icon: "🤖", title: "AI concept mapping", desc: "We verify each resource covers the concepts required for that phase. The quiz is generated from the real content — not guessed." },
              { icon: "🌐", title: "Community signal", desc: "Learners flag outdated resources and suggest better ones. The community votes, the team decides, the best wins." },
              { icon: "♻️", title: "Continuously refreshed", desc: "When a better resource emerges — newer, clearer, more complete — it replaces the old one automatically. Tracks never go stale." },
            ].map((f, i) => (
              <div key={f.title} className="qg-feature" data-aos="fade-up" data-aos-delay={`${i * 60}`}>
                <div className="qg-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="resource-proof" data-aos="fade-up">
            <div className="rp-label">Sample verified resources — VLSI Track</div>
            <div className="rp-items">
              {[
                { src: "NPTEL", label: "VLSI Design Course", dur: "40h", type: "playlist" },
                { src: "nandland", label: "FPGA Fundamentals", dur: "8h", type: "playlist" },
                { src: "MIT OCW", label: "6.004 Computation Structures", dur: "self-paced", type: "docs" },
                { src: "Bro Code", label: "C for Embedded Systems", dur: "4h", type: "playlist" },
                { src: "Xilinx", label: "Vivado Quick Start", dur: "2h", type: "article" },
              ].map(r => (
                <div key={r.label} className="rp-item">
                  <div className="rp-item-top">
                    <span className={`rp-type rp-type--${r.type}`}>{r.type}</span>
                    <span className="rp-verified">✓ verified</span>
                  </div>
                  <div className="rp-item-label">{r.label}</div>
                  <div className="rp-item-meta"><span>{r.src}</span><span>{r.dur}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AI QUIZ ───────────────────────────────────────────── */}
      <section className="quiz-section" data-aos="fade-up">
        <div className="section-inner quiz-inner">
          <div className="quiz-text">
            <div className="section-label">AI Assessments</div>
            <h2 className="section-h2 left">
              Prove you learned it.
              <br />
              <span className="text-accent">Not just watched it.</span>
            </h2>
            <p className="section-sub left">
              After every phase, an AI quiz tests the exact concepts from the resources
              you just studied. Pass with 70%+ to unlock the next phase and raise
              your readiness score. Works for every track — hardware or software.
            </p>
            <ul className="quiz-points">
              <li><span>◆</span> Generated from the actual video content — not generic MCQs</li>
              <li><span>◆</span> Hard difficulty by default — real understanding required</li>
              <li><span>◆</span> Instant explanations for wrong answers so you learn, not just retry</li>
              <li><span>◆</span> Score is logged to your public readiness profile permanently</li>
            </ul>
            <button className="btn-primary" onClick={goStart}>Start a Track Free →</button>
          </div>

          <div className="quiz-demo" data-aos="fade-left" data-aos-delay="100">
            <div className="qd-card">
              <div className="qd-header">
                <span className="qd-phase">⚡ Phase 3 · Sequential Circuits</span>
                <span className="qd-difficulty">Hard</span>
              </div>
              <div className="qd-q-label">Question 2 of 3</div>
              <div className="qd-progress"><div className="qd-prog-fill" style={{ width: "66%" }} /></div>
              <h4 className="qd-question">In a Moore FSM, the output depends on:</h4>
              <div className="qd-options">
                {[
                  { text: "Current input signals only" },
                  { text: "The current state and input combined" },
                  { text: "Current state only, independent of inputs", correct: true, selected: true },
                  { text: "The previous state's output value" },
                ].map((opt, i) => (
                  <div key={i} className={`qd-opt ${opt.selected && opt.correct ? "qd-opt--correct" : ""}`}>
                    <span className="qd-opt-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{opt.text}</span>
                    {opt.selected && opt.correct && <span className="qd-tick">✓</span>}
                  </div>
                ))}
              </div>
              <div className="qd-explanation">
                <span className="qd-exp-icon">💡</span>
                <span>Correct! Moore machines output depends only on the current state — making them simpler to design and analyze than Mealy machines.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMMUNITY ─────────────────────────────────────────── */}
      <section className="contrib-section" data-aos="fade-up">
        <div className="section-inner">
          <div className="section-label">Community</div>
          <h2 className="section-h2">Built by learners. Verified by learners.</h2>
          <p className="section-sub">
            Every resource can be questioned, improved, or replaced. Find something better?
            Submit it. The community votes. The best resource wins — and you get credited.
          </p>
          <div className="contrib-grid">
            <div className="cg-flow" data-aos="fade-right" data-aos-delay="80">
              {[
                { step: "01", title: "You find a better resource", desc: "A clearer explanation, newer tutorial, or missing concept — submit the URL and reason in one click." },
                { step: "02", title: "Community reviews it", desc: "Other learners who've completed that phase review and vote. The Learnlia team has final say." },
                { step: "03", title: "You earn 150 XP + 20 Credits", desc: "Approved contributions earn XP and credits redeemable for Pro features and track unlocks." },
                { step: "04", title: "The track improves for everyone", desc: "Your contribution goes live. Your name is credited publicly in the track's contributor list." },
              ].map(s => (
                <div key={s.step} className="cg-step">
                  <span className="cg-step-num">{s.step}</span>
                  <div><h4>{s.title}</h4><p>{s.desc}</p></div>
                </div>
              ))}
            </div>
            <div className="cg-proof" data-aos="fade-left" data-aos-delay="80">
              <div className="cg-proof-label">Recent contributions approved</div>
              {[
                { user: "Rajasekaran", action: "Added clearer CMOS inverter LTSpice tutorial", track: "VLSI Track · Phase 3", xp: "+150 XP", time: "2h ago" },
                { user: "Nithya", action: "Replaced outdated React hooks video with 2024 version", track: "Frontend Track · Phase 3", xp: "+150 XP", time: "1d ago" },
                { user: "Arjun", action: "Added missing Flexbox cheatsheet", track: "Frontend Track · Phase 1", xp: "+150 XP", time: "3d ago" },
                { user: "Divya", action: "Flagged broken playlist — replacement submitted", track: "Python Track · Phase 2", xp: "+150 XP", time: "5d ago" },
              ].map(c => (
                <div key={c.user} className="cg-contrib-item">
                  <div className="cg-contrib-av">{c.user[0]}</div>
                  <div className="cg-contrib-info">
                    <span className="cg-contrib-action">{c.action}</span>
                    <span className="cg-contrib-track">{c.track}</span>
                  </div>
                  <div className="cg-contrib-right">
                    <span className="cg-xp">{c.xp}</span>
                    <span className="cg-time">{c.time}</span>
                  </div>
                </div>
              ))}
              <button className="cg-contribute-btn" onClick={goStart}>Contribute to a track →</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── PUBLIC PROFILE ────────────────────────────────────── */}
      <section className="profile-section" data-aos="fade-up">
        <div className="section-inner profile-inner">
          <div className="profile-demo" data-aos="fade-right" data-aos-delay="80">
            <div className="pd-card">
              <div className="pd-header">
                <div className="pd-avatar">R</div>
                <div>
                  <div className="pd-name">Rajasekaran M.</div>
                  <div className="pd-handle">learnlia.com/u/rajasekaran</div>
                </div>
                <div className="pd-ready-score">
                  <span className="pd-score-num">86</span>
                  <span className="pd-score-label">Readiness</span>
                </div>
              </div>
              <div className="pd-tracks">
                {[
                  { name: "VLSI Design Fundamentals", percent: 100, color: "#f59e0b", phases: "4/4", done: true },
                  { name: "Frontend Internship Track", percent: 68, color: "#3b82f6", phases: "3/5", quizPass: true },
                  { name: "Embedded Systems Mastery", percent: 40, color: "#10b981", phases: "2/6" },
                ].map(t => (
                  <div key={t.name} className="pd-track">
                    <div className="pd-track-top">
                      <span className="pd-track-name">{t.name}</span>
                      {t.done
                        ? <span className="pd-done-chip">✓ Completed</span>
                        : <span className="pd-track-prog">{t.phases} phases</span>
                      }
                    </div>
                    <div className="pd-bar-wrap">
                      <div className="pd-bar-fill" style={{ width: `${t.percent}%`, background: t.color }} />
                    </div>
                    <div className="pd-track-meta">
                      <span>{t.percent}% complete</span>
                      {t.quizPass && <span className="pd-quiz-pass">🧠 AI quizzes passed</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pd-footer">
                <span>🔥 24 day streak</span>
                <span>⚡ 1,840 XP</span>
                <span>🏆 3 badges</span>
              </div>
            </div>
          </div>
          <div className="profile-text">
            <div className="section-label">Public readiness profile</div>
            <h2 className="section-h2 left">
              Your proof of learning.
              <br />
              <span className="text-accent">Shareable. Verified.</span>
            </h2>
            <p className="section-sub left">
              Every track you complete, every quiz you pass — it builds your public
              Learnlia profile. Share the link. Recruiters see real evidence of
              completion across hardware and software — not just a certificate image.
            </p>
            <ul className="quiz-points">
              <li><span>◆</span> Public URL — <em>learnlia.com/u/yourname</em></li>
              <li><span>◆</span> Shows hardware + software track completion</li>
              <li><span>◆</span> AI quiz scores per phase, publicly verifiable</li>
              <li><span>◆</span> Contributor badges + streak data</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────── */}
      <section className="testimonials" data-aos="fade-up">
        <div className="section-label" style={{ display: "block", textAlign: "center", width: "fit-content", margin: "0 auto 12px" }}>What learners say</div>
        <h2 className="section-h2">Real people. Real completion.</h2>
        <div className="testimonial-track">
          {[
            { text: "The VLSI track structured everything I couldn't find anywhere. CMOS, logic, Verilog — all in one path that actually builds on itself.", name: "Rajasekaran", role: "VLSI track" },
            { text: "I pasted an embedded systems playlist and got a full structured course with quizzes in 30 seconds. Insane.", name: "Karthik", role: "Playlist → Course" },
            { text: "The AI quiz after Phase 3 was genuinely hard. I had to re-watch two videos to pass. That's exactly how it should be.", name: "Arjun", role: "Frontend track" },
            { text: "My profile showing VLSI + frontend completions got noticed. The recruiter said it showed rare breadth.", name: "Uma Rani", role: "Multi-track" },
            { text: "The roadmap feature told me to do Embedded before PCB. That order made everything click.", name: "Divya", role: "Custom Roadmap" },
            { text: "Finally a free platform that doesn't feel cheap. The hardware track curation is insane — everything is actually good.", name: "Nithya", role: "PCB track" },
            { text: "The VLSI track structured everything I couldn't find anywhere. CMOS, logic, Verilog — all in one path that actually builds on itself.", name: "Rajasekaran", role: "VLSI track" },
            { text: "I pasted an embedded systems playlist and got a full structured course with quizzes in 30 seconds. Insane.", name: "Karthik", role: "Playlist → Course" },
            { text: "The AI quiz after Phase 3 was genuinely hard. I had to re-watch two videos to pass. That's exactly how it should be.", name: "Arjun", role: "Frontend track" },
            { text: "My profile showing VLSI + frontend completions got noticed. The recruiter said it showed rare breadth.", name: "Uma Rani", role: "Multi-track" },
            { text: "The roadmap feature told me to do Embedded before PCB. That order made everything click.", name: "Divya", role: "Custom Roadmap" },
            { text: "Finally a free platform that doesn't feel cheap. The hardware track curation is insane — everything is actually good.", name: "Nithya", role: "PCB track" },
          ].map((t, i) => (
            <div className="testimonial" key={i}>
              <div className="t-stars">★★★★★</div>
              <p>"{t.text}"</p>
              <div className="t-meta">
                <span className="t-av">{t.name[0]}</span>
                <div>
                  <span className="t-name">{t.name}</span>
                  <span className="t-role">{t.role}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section className="faq-section" data-aos="fade-up">
        <div className="section-inner faq-inner">
          <div className="section-label" style={{ display: "block", textAlign: "center", width: "fit-content", margin: "0 auto 12px" }}>FAQ</div>
          <h2 className="section-h2" style={{ textAlign: "center", marginBottom: "36px" }}>Common questions</h2>
          {[
            { q: "Is Learnlia actually free?", a: "Yes. All tracks, resources, and core AI quizzes are completely free. Pro unlocks unlimited quiz attempts, advanced analytics, and your full shareable proof profile. The core learning system is free forever." },
            { q: "Does Learnlia have hardware tracks?", a: "Yes — VLSI Design Fundamentals, PCB Design & Prototyping, Embedded Systems Mastery, and FPGA Development are all live. More hardware tracks are shipping in Q3." },
            { q: "How does the Playlist → Course feature work?", a: "Paste a YouTube playlist URL. Learnlia analyses the videos, structures them into logical phases, and generates AI quizzes per phase. Your custom course is live in under 60 seconds." },
            { q: "How does the Custom Roadmap work?", a: "Tell Learnlia your role (e.g. VLSI engineer, frontend developer), your goal, and your current level. It assembles a personalised sequence of existing curated tracks in the right order for your situation." },
            { q: "How does the AI quiz work?", a: "After you complete a phase's resources, you take a quiz. Questions are generated from the actual content of the videos and articles in that phase. You need 70% to pass and unlock the next phase. Wrong answers come with explanations." },
            { q: "What is the Readiness Score?", a: "It's a weighted score (0–100) built from how many phases you've completed and the quiz scores you've earned. Each phase has a readiness weight. Pass everything at full score = 100 readiness." },
          ].map((item, i) => (
            <FAQItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="cta-section" data-aos="fade-up">
        <div className="cta-inner">
          <div className="cta-orb" />
          <div className="section-label" style={{ color: "rgba(255,255,255,0.6)", marginBottom: "16px" }}>
            Start today. It's free.
          </div>
          <h2 className="cta-h2">
            Hardware. Software.<br />One platform. Zero paywalls.
          </h2>
          <p className="cta-sub">
            Pick your track — VLSI, embedded, frontend, ML. Work through the best free resources.
            Pass the AI quiz. Build real proof. Share your profile. No credit card.
          </p>
          <div className="cta-btns">
            <button className="btn-cta-primary" onClick={goStart}>Browse Tracks — It's Free →</button>
            <button className="btn-cta-ghost" onClick={() => navigate("/tracks/public")}>See all tracks</button>
          </div>
          <p className="cta-trust">No credit card · No subscription · 847+ learners active this month</p>
        </div>
      </section>

    </div>
  );
}